import React, {useState} from "react";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {LoadingButton} from "@mui/lab";
import {useSnackbar} from "notistack";
import moment from "moment";
import {cancelVerificationApi, lookupVerificationApi} from "../../../api/invoice";

const EMPTY = {cardNumber: "", info: null};

/**
 * Poništavanje overe koja nije fiskalizovana — briše overu i oslobađa sedište/alotman.
 * Overa sa fiskalnim računom se ovim putem ne dira, nego refundacijom računa.
 */
export default function CancelVerificationDialog({open, onClose}) {
    const {enqueueSnackbar} = useSnackbar();
    const [cardNumber, setCardNumber] = useState(EMPTY.cardNumber);
    const [info, setInfo] = useState(EMPTY.info);
    const [searching, setSearching] = useState(false);
    const [cancelling, setCancelling] = useState(false);

    const errorMessage = (error) =>
        (typeof error === "string" ? error : error?.data?.message || error?.message) || "Došlo je do greške.";

    const handleClose = () => {
        setCardNumber(EMPTY.cardNumber);
        setInfo(EMPTY.info);
        onClose?.();
    };

    const handleSearch = async () => {
        if (!cardNumber.trim()) {
            return enqueueSnackbar("Unesite broj karte.", {variant: "warning"});
        }
        setSearching(true);
        setInfo(null);
        try {
            const {data} = await lookupVerificationApi(cardNumber.trim());
            setInfo(data);
        } catch (error) {
            console.error("lookupVerificationApi", error);
            enqueueSnackbar(errorMessage(error), {variant: "warning"});
        } finally {
            setSearching(false);
        }
    };

    const handleCancel = async () => {
        setCancelling(true);
        try {
            await cancelVerificationApi(info.externalNumber);
            enqueueSnackbar(`Overa karte ${info.externalNumber} je poništena.`, {variant: "success"});
            handleClose();
        } catch (error) {
            console.error("cancelVerificationApi", error);
            enqueueSnackbar(errorMessage(error), {variant: "warning"});
        } finally {
            setCancelling(false);
        }
    };

    const rows = info ? [
        {label: "Broj karte", value: info.externalNumber},
        {label: "Relacija", value: [info.departureStationName, info.arrivalStationName].filter(Boolean).join(" - ") || "-"},
        {label: "Datum polaska", value: info.journeyDate ? moment(info.journeyDate).format("DD.MM.YYYY") : "-"},
        {label: "Sedište", value: info.seat ?? "-"},
        {label: "Overio", value: info.verifiedBy || "-"},
        {label: "Vreme overe", value: info.verifiedAt ? moment(info.verifiedAt).format("DD.MM.YYYY HH:mm") : "-"},
        {label: "Broj računa", value: info.invoiceNumber || "-"},
    ] : [];

    return (
        <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
            <DialogContent>
                <Stack spacing={2}>
                    <Typography textAlign="center" variant="body2" fontWeight="bold">
                        Poništavanje overe
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            fullWidth
                            autoFocus
                            label="Broj karte"
                            helperText="Broj sa kupona overe ili ID karte"
                            value={cardNumber}
                            onChange={(e) => {
                                setCardNumber(e.target.value);
                                setInfo(null);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleSearch();
                                }
                            }}
                        />
                        <LoadingButton
                            variant="outlined"
                            loading={searching}
                            onClick={handleSearch}
                            sx={{height: 56, minWidth: 120}}
                        >
                            Pronađi
                        </LoadingButton>
                    </Stack>

                    {info && (
                        <>
                            <Divider/>
                            <Stack spacing={0.5}>
                                {rows.map((row) => (
                                    <Stack key={row.label} direction="row" justifyContent="space-between">
                                        <Typography variant="body2" color="text.secondary">{row.label}</Typography>
                                        <Typography variant="body2" fontWeight="bold">{row.value}</Typography>
                                    </Stack>
                                ))}
                            </Stack>
                            {info.fiscalized && (
                                <Alert severity="warning">
                                    Ova overa ima fiskalni račun — poništava se refundacijom računa, ne ovim putem.
                                </Alert>
                            )}
                        </>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button color="inherit" onClick={handleClose}>Zatvori</Button>
                <LoadingButton
                    variant="contained"
                    color="error"
                    loading={cancelling}
                    disabled={!info || info.fiscalized}
                    onClick={handleCancel}
                >
                    Poništi overu
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
